import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import {
    CircleSpinner,
    ElementAttributes,
    Locales,
    Navigation,
    NavigationPage,
    Portal,
    useSession,
} from '@ratehub/base-ui';
import {
    COMMON_BUTTON_MESSAGES,
    BrokerPropType,
    InsurerPropType,
    InsuranceSpinner,
    ProviderPhoneNumberBlock,
    CIS_PHONE_NUMBER,
} from '@ratehub/ins-common';

import ErrorModalDefault from '../components/ErrorModalDefault';
import PAGES from '../definitions/Pages';
import PROVIDER_IDS from '../definitions/ProviderIds';
import CoverageDetailsPage from '../pages/CoverageDetailsPage';
import PersonalDetailsPage from '../pages/PersonalDetailsPage';
import ConfirmationPage from '../pages/ConfirmationPage';
import createContext from '../applications/createContext';
import LifeAutofillButton from '../components/LifeAutofillButton';
import withWizardLayout from '../functions/withWizardLayout';


// Wizard layout wrappers
const CoverageDetailsPageWithWizardLayout = withWizardLayout(CoverageDetailsPage);
const PersonalDetailsPageWithWizardLayout = withWizardLayout(PersonalDetailsPage);

function InsLifeWizard({
    isRoutingEnabled = true,
    locale = Locales.ENGLISH,
    brokers,
    insurers,

    onCreateState,
}) {
    const sessionStore = useSession();

    const [ context ] = useState(() => createContext({
        sessionStore,
        brokers,
        insurers,
    }));

    const [ isDebugEnabled, setIsDebugEnabled ] = useState(false);

    // Allow access to the outside (same idea as ref in React)
    useEffect(
        () => {
            if (context) {
                onCreateState?.(context);
            }
        },
        [ context ],
    );

    const viewStore = context.viewStore;
    const breadcrumbPages = viewStore
        ? [
            viewStore.coverageDetails,
            viewStore.personalDetails,
        ]
        : [];

    const BASE_ROUTE = locale === Locales.FRENCH
        ? PAGES.BASE_ROUTE_FR
        : PAGES.BASE_ROUTE; // Default to English

    function getLayoutProps() {
        return {
            breadcrumbPages,
            lastEditDate: context.lastEditDate,
            insuranceType: context.insuranceType,
        };
    }

    return (
        <>
            <If condition={viewStore?.shouldShowPhoneNumber}>
                <Portal id={ElementAttributes.HEADER_PORTAL_ID_CENTER}>
                    <ProviderPhoneNumberBlock
                        phoneNumber={CIS_PHONE_NUMBER}
                        isPadded={true}
                    />
                </Portal>
            </If>

            <If condition={context.hasError}>
                <ErrorModalDefault
                    provider={(
                        context.provider
                        // Default to our primary provider as backup
                        || brokers.find(broker => broker.id === PROVIDER_IDS.PRIMARY_PROVIDER)
                    )}
                    onClose={() => context.setHasError(false)}
                />
            </If>

            <Choose>
                <When condition={!context.isReady}>
                    <CircleSpinner />
                </When>

                <Otherwise>
                    <If condition={context.isSubmittingLead}>
                        <InsuranceSpinner
                            title={(
                                <FormattedMessage
                                    id="InsLifeWizard.submissionSpinner.title"
                                    defaultMessage="We’re submitting your quote request to our trusted life insurance partner"
                                />
                            )}
                            isSpinning
                            isFullScreen
                            centreContent
                        />
                    </If>

                    <Navigation
                        baseRoute={BASE_ROUTE}
                        activePageName={viewStore.activePageName}
                        fallbackPageName={PAGES.COVERAGE_DETAILS.id}
                        isRoutingEnabled={isRoutingEnabled}
                        onLoad={async ({
                            relativeParams,
                            isDebug,
                        }) => {
                            setIsDebugEnabled(isDebug);

                            const insuranceType = relativeParams[0]?.toLowerCase();
                            await context.loadDocument(insuranceType);
                        }}
                    >
                        <Choose>
                            {/* Since the form type is determined in the onLoad above,
                            we need a second spinner to wait for that to be determined*/}
                            <When condition={!context.formType}>
                                <CircleSpinner />
                            </When>

                            <Otherwise>
                                {/* Autofill button */}
                                <If condition={isDebugEnabled}>
                                    <LifeAutofillButton
                                        viewModel={viewStore.activePage}
                                    />
                                </If>

                                {/* Coverage Details Page */}
                                <NavigationPage
                                    id={PAGES.COVERAGE_DETAILS.id}
                                    route={PAGES.COVERAGE_DETAILS.route}
                                    title={(
                                        <FormattedMessage
                                            id="StandardLifeInsuranceWizard.Navigation.CoverageDetailsPage"
                                            defaultMessage="Life Insurance | Coverage Details"
                                        />
                                    )}
                                    onActivate={viewStore.showCoverageDetailsPage}
                                    routeParams={{ insuranceType: context.insuranceType }}
                                >
                                    <CoverageDetailsPageWithWizardLayout
                                        layoutProps={getLayoutProps()}

                                        formType={context.formType}
                                        values={viewStore.coverageDetails.values}
                                        onBack={viewStore.isBackEnabled ? viewStore.coverageDetails.back : undefined}
                                        onSubmit={viewStore.coverageDetails.submit}
                                        continueButtonMessage={viewStore.isJourneyComplete
                                            ? COMMON_BUTTON_MESSAGES.UPDATE
                                            : COMMON_BUTTON_MESSAGES.CONTINUE
                                        }
                                    />
                                </NavigationPage>

                                {/* Personal Details Page */}
                                <NavigationPage
                                    id={PAGES.PERSONAL_DETAILS.id}
                                    route={PAGES.PERSONAL_DETAILS.route}
                                    title={(
                                        <FormattedMessage
                                            id="StandardLifeInsuranceWizard.Navigation.PersonalDetailsPage"
                                            defaultMessage="Life Insurance | Personal Details"
                                        />
                                    )}
                                    onActivate={viewStore.showPersonalDetailsPage}
                                    routeParams={{ insuranceType: context.insuranceType }}
                                >
                                    <PersonalDetailsPageWithWizardLayout
                                        layoutProps={getLayoutProps()}

                                        hasJointApplicant={viewStore.isJointPolicy}
                                        shouldShowGenderQuestion={viewStore.personalDetails.shouldShowGenderQuestion}
                                        values={viewStore.personalDetails.values}
                                        onBack={viewStore.isBackEnabled ? viewStore.personalDetails.back : undefined}
                                        onSubmit={viewStore.personalDetails.submit}
                                        continueButtonMessage={viewStore.isJourneyComplete
                                            ? COMMON_BUTTON_MESSAGES.UPDATE
                                            : COMMON_BUTTON_MESSAGES.CONTINUE
                                        }
                                    />
                                </NavigationPage>

                                {/* Confirmation Page */}
                                <NavigationPage
                                    id={PAGES.CONFIRMATION.id}
                                    route={PAGES.CONFIRMATION.route}
                                    title={(
                                        <FormattedMessage
                                            id="StandardLifeInsuranceWizard.Navigation.ConfirmationPage"
                                            defaultMessage="Life Insurance | Confirmation"
                                        />
                                    )}
                                    onActivate={viewStore.showConfirmationPage}
                                    routeParams={{ insuranceType: context.insuranceType }}
                                >
                                    <ConfirmationPage
                                        hasJointApplicant={viewStore.isJointPolicy}
                                        values={viewStore.confirmation.values}
                                    />
                                </NavigationPage>
                            </Otherwise>
                        </Choose>
                    </Navigation>
                </Otherwise>
            </Choose>
        </>
    );
}

InsLifeWizard.propTypes = {
    isRoutingEnabled: PropTypes.bool,
    locale: PropTypes.string,
    brokers: PropTypes.arrayOf(BrokerPropType),
    insurers: PropTypes.arrayOf(InsurerPropType),
    onCreateState: PropTypes.func,
};

export default observer(InsLifeWizard);
